import { FC, useState, useEffect } from 'react';
import { format, parseISO } from 'date-fns';
import { LoadingTable } from '../../Core/Components/LoadingTable/LoadingTable';
import {
	ProductEntry,
	Location,
	StoreOrder,
	LineItem,
} from '../../Core/SharedTypes/Models';
import { useGetMyLocations } from '../../Core/Hooks/LocationHooks';
import { useApproveLineItem } from '../../Core/Hooks/OrderBookHooks';

import './PlannerPageTable.scss';
import { Lightswitch } from '../../Core/Components/Lightswitch/Lightswitch';
import { Icon } from '../../Core/Components/Icon/Icon';
import { NullScreen } from '../../Core/Components/NullScreen/NullScreen';

export type Props = {
	currentLocation: Location | undefined;
	orders: StoreOrder[] | undefined;
	isLoading: boolean;
	handleOrderClick: (order: StoreOrder) => void;
};

export const PlannerPageTable: FC<Props> = ({
	orders,
	isLoading,
	handleOrderClick,
}) => {
	const { data: locations } = useGetMyLocations();
	const [stateOrders, setStateOrders] = useState<StoreOrder[] | undefined>(
		undefined,
	);

	useEffect(() => {
		if (orders) {
			const cloned = structuredClone(orders);
			cloned.forEach((order) => {
				if (order.lineItems) {
					order.lineItems = order.lineItems.filter((li) => li.approved == 0);
				}
			});
			setStateOrders(cloned);
		} else {
			setStateOrders(undefined);
		}
	}, [orders]);

	const handlePlanning = (
		orderIndex: number,
		lineItemIndex: number,
		key: string,
		newValue: boolean,
	) => {
		if (!stateOrders || !orders) {
			return;
		}

		if (key === 'plannedInBakery') {
			stateOrders[orderIndex].lineItems[lineItemIndex].plannedInBakery =
				newValue ? 1 : 0;
		} else {
			stateOrders[orderIndex].lineItems[lineItemIndex].plannedInPastry =
				newValue ? 1 : 0;
		}

		setStateOrders([...stateOrders]);
	};

	const { approveLineItem, isApproving } = useApproveLineItem();

	const handleApprove = async (orderIndex: number, lineItemIndex: number) => {
		if (!stateOrders || isApproving) {
			return;
		}

		const lineItem = stateOrders[orderIndex].lineItems[lineItemIndex];

		if (lineItem.plannedInBakery == 0 && lineItem.plannedInPastry == 0) {
			alert('Vælg et planlægningsområde');
			return;
		}

		try {
			const result = await approveLineItem(
				stateOrders[orderIndex].lineItems[lineItemIndex],
			);
			if (result) {
				// Update line item approved in state
				stateOrders[orderIndex].lineItems[lineItemIndex].approved = 1;
				setStateOrders([...stateOrders]);

				// Remove line item from state with a delay for animatio purposes
				setTimeout(() => {
					stateOrders[orderIndex].lineItems.splice(lineItemIndex, 1);
					setStateOrders([...stateOrders]);
				}, 300);
			}
		} catch (error) {
			console.error(error);
		}
	};

	// First, filter the locations outside the JSX
	const locationsWithPlannedOrders = locations
		? locations.filter((location) => {
				// Check if there are any orders for this location
				const locationOrders: StoreOrder[] = stateOrders
					? stateOrders.filter(
							(order) => order && order.locationId == location.id,
						)
					: [];
				// const locationOrders : StoreOrder[] = orders ? orders.filter(order => order && order.locationId == location.id) : [];

				if (locationOrders.length === 0) {
					return false; // No orders for this location
				}

				// Check if any order has at least one line item with planning value of 1
				return locationOrders.some((order) => {
					// Make sure order and lineItems exist
					if (!order || !order.lineItems || !order.lineItems.length) {
						return false;
					}

					// Check if any lineItem has plannedInBakery or plannedInPastry set to 1
					return order.lineItems.some((lineItem) => lineItem.planning == 1);
				});
			})
		: [];

	const handleOpenSlideIn = (localOrder: StoreOrder) => {
		const originalOrder = orders?.find((o) => o.id === localOrder.id);
		if (originalOrder) {
			const orderToSave = structuredClone(originalOrder);

			orderToSave.lineItems.forEach((origItem) => {
				const localItem = localOrder.lineItems.find(
					(li) => li.id === origItem.id,
				);

				if (localItem) {
					origItem.plannedInBakery = localItem.plannedInBakery;
					origItem.plannedInPastry = localItem.plannedInPastry;
					origItem.approved = localItem.approved;
				} else {
					origItem.approved = 1;
				}
			});

			handleOrderClick(orderToSave);
		} else {
			handleOrderClick(localOrder);
		}
	};

	return (
		<div className="planner-table">
			{/* LOADING */}
			{isLoading && <LoadingTable />}

			{/* LOCATION */}
			{locationsWithPlannedOrders && locationsWithPlannedOrders.length > 0 ? (
				locationsWithPlannedOrders.map(({ id, title }) => {
					return (
						<div className="planner-table__locations" key={title}>
							{title}

							<div className="planner-table__header">
								<div className="header-title">Afhentning</div>
								<div className="header-title">Ordrenummer</div>
								<div className="header-title">Antal</div>
								<div className="header-title">Bestilling</div>
								<div className="header-title center">Bageri</div>
								<div className="header-title center">Konditori</div>
								<div className="header-title center">Godkend</div>
							</div>

							{/* ORDER*/}
							{!isLoading &&
								stateOrders &&
								stateOrders.map((order: StoreOrder, orderIndex: number) => {
									const hasVisibleItems = order?.lineItems?.some(
										(li) => li.planning == 1,
									);

									return (
										order &&
										order.locationId == id &&
										hasVisibleItems && (
											<div key={order.id} className="planner-table__orders">
												{/* LINEITEM */}
												<div className="planner-table__lineitems">
													{order &&
														order.lineItems &&
														order.lineItems.map(
															(lineItem: LineItem, lineItemIndex: number) => {
																if (lineItem.planning == 0) return null;
																return (
																	<div
																		key={lineItem.id}
																		className={
																			lineItem.approved == 1
																				? 'lineitem lineitem--approved'
																				: 'lineitem'
																		}
																	>
																		<div className="lineitem__column lineitem__date-time">
																			<span className="date">
																				{format(
																					parseISO(order.pickupTime),
																					'dd.MM.yy',
																				)}
																			</span>
																			<span className="time">
																				{format(
																					parseISO(order.pickupTime),
																					'HH.mm',
																				)}
																			</span>
																		</div>

																		<div className="lineitem__column lineitem__ordernumber">
																			{order.orderNumber}
																			<Icon
																				name="Info"
																				width={22}
																				onClick={() => {
																					handleOpenSlideIn(order);
																				}}
																			></Icon>
																		</div>

																		<div className="lineitem__column lineitem__qty">
																			{lineItem.qty}
																		</div>

																		<div className="lineitem__column lineitem__description">
																			{lineItem.description}
																		</div>

																		<div className="lineitem__column lineitem__planned">
																			<Lightswitch
																				text=""
																				value={
																					lineItem.plannedInBakery == 1
																						? true
																						: false
																				}
																				onToggle={(newValue) =>
																					handlePlanning(
																						orderIndex,
																						lineItemIndex,
																						'plannedInBakery',
																						newValue,
																					)
																				}
																			/>
																		</div>

																		<div className="lineitem__column lineitem__planned">
																			<Lightswitch
																				text=""
																				value={
																					lineItem.plannedInPastry == 1
																						? true
																						: false
																				}
																				onToggle={(newValue) =>
																					handlePlanning(
																						orderIndex,
																						lineItemIndex,
																						'plannedInPastry',
																						newValue,
																					)
																				}
																			/>
																		</div>

																		<div className="lineitem__column lineitem__approve">
																			<Icon
																				name="CheckmarkHollow"
																				width={36}
																				onClick={() => {
																					handleApprove(
																						orderIndex,
																						lineItemIndex,
																					);
																				}}
																			/>
																		</div>
																	</div>
																);
															},
														)}
												</div>
											</div>
										)
									);
								})}
						</div>
					);
				})
			) : !isLoading ? (
				<NullScreen text="Ingen bestillinger til planlægning" />
			) : null}
		</div>
	);
};
